// apps/provider/src/services/api/homeHealth.js
// Home Health (agency of record): episodes, certification and payment periods,
// physician orders. Every endpoint is solution-gated server-side
// (hh_require_access): orgs without the Home Health solution get 403.
//
// Xano tables: hh_episode, hh_certification_period, hh_payment_period,
// hh_physician_order, hh_episode_inpatient_stay
// Response types: @prism-ehr/shared → HhEpisodeListResponse,
// HhEpisodeDetailResponse, HhAdmitEpisodeResponse, HhDischargeEpisodeResponse
//
// Dates in and out of these endpoints are 'YYYY-MM-DD' strings.

// The worklist ships as a dark Tier 1 twin; flip to '/hh/worklist' after the
// production loadcheck and swap (docs/QA_STRATEGY.md, endpoint tiering).
export const HH_WORKLIST_PATH = '/hh/worklist-v2';

/**
 * Creates Home Health methods. Mixed into ApiService via Object.assign().
 */
export function createHomeHealthMethods() {
  return {

    // ==========================================
    // EPISODES
    // ==========================================

    /**
     * Paged list of the organization's home health episodes, newest first.
     * Xano: GET /hh/episodes
     * @param {Object} [filters]
     * @param {string} [filters.patient_id]
     * @param {number} [filters.case_id]
     * @param {string} [filters.status] - Comma-separated statuses
     * @param {string} [filters.soc_from] - YYYY-MM-DD
     * @param {string} [filters.soc_to] - YYYY-MM-DD
     * @param {number} [filters.page]
     * @param {number} [filters.per_page] - Max 100
     * @returns {Promise<import('@prism-ehr/shared').HhEpisodeListResponse>}
     */
    async listHhEpisodes(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.case_id) queryParams.append('case_id', String(filters.case_id));
      if (filters.status) queryParams.append('status', filters.status);
      if (filters.soc_from) queryParams.append('soc_from', filters.soc_from);
      if (filters.soc_to) queryParams.append('soc_to', filters.soc_to);
      if (filters.page) queryParams.append('page', String(filters.page));
      if (filters.per_page) queryParams.append('per_page', String(filters.per_page));
      const qs = queryParams.toString();
      return this.request(`/hh/episodes${qs ? `?${qs}` : ''}`, { method: 'GET' });
    },

    /**
     * Create an episode (status referred) for an open home_health case.
     * Refused when the case already has an open episode. A physician-ordered
     * SOC date also creates the start-of-care order.
     * Xano: POST /hh/episodes
     * @param {import('@prism-ehr/shared').HhCreateEpisodeRequest} body
     * @returns {Promise<import('@prism-ehr/shared').HhCreateEpisodeResponse>}
     */
    async createHhEpisode(body) {
      return this.request('/hh/episodes', {
        method: 'POST',
        body: JSON.stringify(body)
      }, 'createHhEpisode');
    },

    /**
     * One episode with its periods, stays, orders and NOA status.
     * Xano: GET /hh/episodes/{episode_id}
     * @param {number} episodeId
     * @returns {Promise<import('@prism-ehr/shared').HhEpisodeDetailResponse>}
     */
    async getHhEpisode(episodeId) {
      return this.request(`/hh/episodes/${episodeId}`, { method: 'GET' });
    },

    /**
     * Admit an episode: sets the start of care and creates certification 1
     * with payment periods 1 and 2.
     * Xano: POST /hh/episodes/{episode_id}/admit
     * @param {number} episodeId
     * @param {string} socDate - YYYY-MM-DD
     * @returns {Promise<import('@prism-ehr/shared').HhAdmitEpisodeResponse>}
     */
    async admitHhEpisode(episodeId, socDate) {
      return this.request(`/hh/episodes/${episodeId}/admit`, {
        method: 'POST',
        body: JSON.stringify({ soc_date: socDate })
      }, 'admitHhEpisode');
    },

    /**
     * Discharge an episode (or record a death with discharge_reason "deceased").
     * Closes the periods at the date and discharges the case.
     * Xano: POST /hh/episodes/{episode_id}/discharge
     * @param {number} episodeId
     * @param {import('@prism-ehr/shared').HhDischargeEpisodeRequest} body
     * @returns {Promise<import('@prism-ehr/shared').HhDischargeEpisodeResponse>}
     */
    async dischargeHhEpisode(episodeId, body) {
      return this.request(`/hh/episodes/${episodeId}/discharge`, {
        method: 'POST',
        body: JSON.stringify(body)
      }, 'dischargeHhEpisode');
    },

    /**
     * Sparse update of an episode's intake and documentation fields.
     * Xano: PATCH /hh/episodes/{episode_id}
     * @param {number} episodeId
     * @param {import('@prism-ehr/shared').HhUpdateEpisodeRequest} body
     * @returns {Promise<import('@prism-ehr/shared').HhEpisode>}
     */
    async updateHhEpisode(episodeId, body) {
      return this.request(`/hh/episodes/${episodeId}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
      }, 'updateHhEpisode');
    },

    /**
     * Transfer to an inpatient facility: hold the episode, or end it when
     * discharged = true.
     * Xano: POST /hh/episodes/{episode_id}/transfer
     * @param {number} episodeId
     * @param {import('@prism-ehr/shared').HhTransferEpisodeRequest} body
     * @returns {Promise<import('@prism-ehr/shared').HhEpisodeStaysResponse>}
     */
    async transferHhEpisode(episodeId, body) {
      return this.request(`/hh/episodes/${episodeId}/transfer`, {
        method: 'POST',
        body: JSON.stringify(body)
      }, 'transferHhEpisode');
    },

    /**
     * Resumption of care after an inpatient stay.
     * Xano: POST /hh/episodes/{episode_id}/resume
     * @param {number} episodeId
     * @param {import('@prism-ehr/shared').HhResumeEpisodeRequest} body
     * @returns {Promise<import('@prism-ehr/shared').HhEpisodeStaysResponse>}
     */
    async resumeHhEpisode(episodeId, body) {
      return this.request(`/hh/episodes/${episodeId}/resume`, {
        method: 'POST',
        body: JSON.stringify(body)
      }, 'resumeHhEpisode');
    },

    /**
     * Open the next 60-day certification period.
     * Xano: POST /hh/episodes/{episode_id}/recertify
     * @param {number} episodeId
     * @returns {Promise<import('@prism-ehr/shared').HhRecertifyEpisodeResponse>}
     */
    async recertifyHhEpisode(episodeId) {
      return this.request(`/hh/episodes/${episodeId}/recertify`, {
        method: 'POST',
        body: JSON.stringify({})
      }, 'recertifyHhEpisode');
    },

    /**
     * Close a referral that will not be admitted.
     * Xano: POST /hh/episodes/{episode_id}/non-admit
     * @param {number} episodeId
     * @param {string} [reason]
     * @returns {Promise<import('@prism-ehr/shared').HhNonAdmitEpisodeResponse>}
     */
    async nonAdmitHhEpisode(episodeId, reason) {
      return this.request(`/hh/episodes/${episodeId}/non-admit`, {
        method: 'POST',
        body: JSON.stringify(reason ? { reason } : {})
      }, 'nonAdmitHhEpisode');
    },

    /**
     * Biller override of a payment period's PDGM timing and/or admission source.
     * Xano: PATCH /hh/episodes/{episode_id}/periods/{period_id}
     * @param {number} episodeId
     * @param {number} periodId
     * @param {import('@prism-ehr/shared').HhOverridePeriodRequest} body
     * @returns {Promise<import('@prism-ehr/shared').HhPaymentPeriod>}
     */
    async overrideHhPeriod(episodeId, periodId, body) {
      return this.request(`/hh/episodes/${episodeId}/periods/${periodId}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
      }, 'overrideHhPeriod');
    },

    // ==========================================
    // PHYSICIAN ORDERS
    // ==========================================

    /**
     * Record a physician order on an episode (get-or-create when
     * plan_of_care_id is sent).
     * Xano: POST /hh/orders
     * @param {import('@prism-ehr/shared').HhCreateOrderRequest} body
     * @returns {Promise<import('@prism-ehr/shared').HhCreateOrderResponse>}
     */
    async createHhOrder(body) {
      return this.request('/hh/orders', {
        method: 'POST',
        body: JSON.stringify(body)
      }, 'createHhOrder');
    },

    /**
     * Paged list of the organization's physician orders, newest first.
     * Xano: GET /hh/orders
     * @param {Object} [filters]
     * @param {number} [filters.hh_episode_id]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.plan_of_care_id] - The 485 order of one plan of care
     * @param {string} [filters.order_status] - Comma-separated statuses
     * @param {boolean} [filters.overdue] - Awaiting signature and past due
     * @param {number} [filters.page]
     * @param {number} [filters.per_page]
     * @returns {Promise<import('@prism-ehr/shared').HhOrderListResponse>}
     */
    async listHhOrders(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.hh_episode_id) queryParams.append('hh_episode_id', String(filters.hh_episode_id));
      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.plan_of_care_id) queryParams.append('plan_of_care_id', filters.plan_of_care_id);
      if (filters.order_status) queryParams.append('order_status', filters.order_status);
      if (filters.overdue) queryParams.append('overdue', 'true');
      if (filters.page) queryParams.append('page', String(filters.page));
      if (filters.per_page) queryParams.append('per_page', String(filters.per_page));
      const qs = queryParams.toString();
      return this.request(`/hh/orders${qs ? `?${qs}` : ''}`, { method: 'GET' });
    },

    /**
     * Edit a draft or pending order, finalize a draft, or void an order.
     * Xano: PATCH /hh/orders/{order_id}
     * @param {number} orderId
     * @param {import('@prism-ehr/shared').HhUpdateOrderRequest} body
     * @returns {Promise<import('@prism-ehr/shared').HhPhysicianOrder>}
     */
    async updateHhOrder(orderId, body) {
      return this.request(`/hh/orders/${orderId}`, {
        method: 'PATCH',
        body: JSON.stringify(body)
      }, 'updateHhOrder');
    },

    /**
     * Record that an order was sent for signature.
     * Xano: POST /hh/orders/{order_id}/send
     * @param {number} orderId
     * @param {import('@prism-ehr/shared').HhSendOrderRequest} body
     * @returns {Promise<import('@prism-ehr/shared').HhPhysicianOrder>}
     */
    async sendHhOrder(orderId, body) {
      return this.request(`/hh/orders/${orderId}/send`, {
        method: 'POST',
        body: JSON.stringify(body)
      }, 'sendHhOrder');
    },

    /**
     * Record the physician's signature, optionally with the returned document.
     * Xano: POST /hh/orders/{order_id}/sign
     * @param {number} orderId
     * @param {import('@prism-ehr/shared').HhSignOrderRequest} body
     * @returns {Promise<import('@prism-ehr/shared').HhPhysicianOrder>}
     */
    async signHhOrder(orderId, body) {
      return this.request(`/hh/orders/${orderId}/sign`, {
        method: 'POST',
        body: JSON.stringify(body)
      }, 'signHhOrder');
    },

    // ==========================================
    // WORKLIST
    // ==========================================

    /**
     * The organization's home health worklist (seven buckets).
     * Xano: GET /hh/worklist-v2 (dark twin; see HH_WORKLIST_PATH)
     * @returns {Promise<import('@prism-ehr/shared').HhWorklistResponse>}
     */
    async getHhWorklist() {
      return this.request(HH_WORKLIST_PATH, { method: 'GET' });
    },
  };
}
